import React, { useEffect, useState } from "react";
import { MdAdd } from "@react-icons/all-files/md/MdAdd";
import "./App.css";
import Header from "./components/Header";
import List from "./components/List";
import Button from "./components/Button";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import RemoveModal from "./components/Modal/RemoveModal";
import EmptyState from "./components/EmptyState";
import ModalWithInput from "./components/Modal/ModalWithInput";
import { ListType } from "./types";

function App() {
  const [bikersWaiting, setBikersWaiting] = useState<string[]>(() => {
    const saved = localStorage.getItem("bikersWaiting") || JSON.stringify([]);
    const initialValue = JSON.parse(saved);
    return initialValue || [];
  });
  const [bikersOnTheRoad, setBikersOnTheRoad] = useState<string[]>(() => {
    const saved = localStorage.getItem("bikersOnTheRoad") || JSON.stringify([]);
    const initialValue = JSON.parse(saved);
    return initialValue || [];
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [bikerName, setBikerName] = useState("");

  const isAllEmpty = ![...bikersWaiting, ...bikersOnTheRoad].length;

  useEffect(() => {
    localStorage.setItem("bikersWaiting", JSON.stringify(bikersWaiting));
  }, [bikersWaiting]);

  useEffect(() => {
    localStorage.setItem("bikersOnTheRoad", JSON.stringify(bikersOnTheRoad));
  }, [bikersOnTheRoad]);

  const onListAdd = () => {
    setBikersWaiting([...bikersWaiting, bikerName]);
    onCloseAddModal();
  };

  const onClear = () => {
    setBikersWaiting([]);
    setBikersOnTheRoad([]);
    setIsRemoveModalOpen(false);
  };

  const onCloseAddModal = () => {
    setIsAddModalOpen(false);
    setBikerName("");
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) {
      const list =
        source.droppableId === "waiting" ? bikersWaiting : bikersOnTheRoad;
      const setList =
        source.droppableId === "waiting"
          ? setBikersWaiting
          : setBikersOnTheRoad;
      if (destination.index === source.index) return;
      const updatedList = [...list];
      const [movedItem] = updatedList.splice(source.index, 1);
      if (!movedItem) return;
      updatedList.splice(destination.index, 0, movedItem);
      setList(updatedList);
    } else {
      const fromList =
        source.droppableId === "waiting" ? bikersWaiting : bikersOnTheRoad;
      const toList =
        destination.droppableId === "waiting" ? bikersWaiting : bikersOnTheRoad;

      const setFromList =
        source.droppableId === "waiting"
          ? setBikersWaiting
          : setBikersOnTheRoad;
      const setToList =
        destination.droppableId === "waiting"
          ? setBikersWaiting
          : setBikersOnTheRoad;

      const updatedFromList = [...fromList];
      const updatedToList = [...toList];
      const [movedItem] = updatedFromList.splice(source.index, 1);
      if (!movedItem) return;
      updatedToList.splice(destination.index, 0, movedItem);
      setFromList(updatedFromList);
      setToList(updatedToList);
    }
  };

  const onRemoveBiker = (index: number, listType: ListType) => {
    const list = {
      waiting: bikersWaiting,
      onTheRoad: bikersOnTheRoad,
    }[listType];

    const setList = {
      waiting: setBikersWaiting,
      onTheRoad: setBikersOnTheRoad,
    }[listType];

    setList(list.filter((_e, i) => i !== index));
  };

  const onEditBiker = (index: number, name: string, listType: ListType) => {
    const list = {
      waiting: bikersWaiting,
      onTheRoad: bikersOnTheRoad,
    }[listType];

    const setList = {
      waiting: setBikersWaiting,
      onTheRoad: setBikersOnTheRoad,
    }[listType];

    const updatedList = [...list];
    updatedList[index] = name;
    setList(updatedList);
  };

  return (
    <div className="App">
      <Header>
        <div className="flex flex-row items-center">
          <img src="logo.svg" alt="logo" />
          <h1 className="ml-2 font-bold">Davez</h1>
        </div>
      </Header>
      <DragDropContext onDragEnd={onDragEnd}>
        <div>
          <div className="container mx-auto w-11/12 pt-4">
            <div className="flex flex-row items-center justify-between pb-5">
              <h1 className="font-bold">Na Lista</h1>
              {isAllEmpty ? null : (
                <Button
                  type="underlined"
                  onClick={() => setIsRemoveModalOpen(true)}
                  label="Limpar lista"
                />
              )}
            </div>
            {!bikersWaiting.length ? (
              <EmptyState
                text={
                  bikersOnTheRoad.length
                    ? "A lista está vazia"
                    : `Ainda não tem ninguém na lista da vez. Clique no botão abaixo para adicionar!`
                }
              />
            ) : null}
            <List
              items={bikersWaiting}
              onRemove={(index) => onRemoveBiker(index, "waiting")}
              onEdit={(index, name) => onEditBiker(index, name, "waiting")}
              type="waiting"
            />
            <div className="w-full flex flex-row justify-center pb-4 pt-1">
              <Button
                label={isAllEmpty ? "Adicionar" : "Adicionar novo"}
                onClick={() => setIsAddModalOpen(true)}
                type={isAllEmpty ? "primary" : "transparent"}
                icon={
                  <MdAdd
                    className="mr-1"
                    fontSize={isAllEmpty ? 16 : 24}
                    fontWeight={100}
                  />
                }
                size="mini"
              />
            </div>
          </div>
          {isAllEmpty ? null : (
            <div className="container mx-auto w-11/12 pt-4">
              <div className="flex flex-row items-center justify-between pb-5">
                <h1 className="font-bold">Na Pista</h1>
              </div>
              <List
                items={bikersOnTheRoad}
                onRemove={(index) => onRemoveBiker(index, "onTheRoad")}
                onEdit={(index, name) => onEditBiker(index, name, "onTheRoad")}
                type="onTheRoad"
                withNumber={false}
              />
              {!bikersOnTheRoad.length ? (
                <p className="text-sm">Ninguém na pista</p>
              ) : null}
            </div>
          )}
        </div>
      </DragDropContext>
      <ModalWithInput
        onChange={(event) => setBikerName(event.target.value)}
        onSubmit={onListAdd}
        onClose={() => onCloseAddModal()}
        isOpen={isAddModalOpen}
        inputLabel="Nome"
        buttonLabel="Adicionar"
      />
      <RemoveModal
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={() => onClear()}
        isOpen={isRemoveModalOpen}
        title="Tem certeza que deseja limpar a lista?"
        description="Todos os nomes na lista e na pista serão excluídos e não tem como desfazer essa ação"
        buttonLabel="Limpar"
      />
    </div>
  );
}

export default App;
